import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';

function InvoiceAdd({ onInvoiceAdded, userId, preselectedCustomerId, editMode = false, initialData = null }) {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(preselectedCustomerId || '');
  const [date, setDate] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([
    { id: 1, product_name: '', quantity: '', unit_price: '', description: '', item_total_price: 0 }
  ]); // Added item_total_price for each item
  const [message, setMessage] = useState('');
  const [overallTotalPrice, setOverallTotalPrice] = useState(0); // Overall total price
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // Calculate overall total price whenever invoiceItems change
  useEffect(() => {
    let total = 0;
    invoiceItems.forEach(item => {
      total += item.item_total_price; // Sum individual item total prices
    });
    setOverallTotalPrice(total);
  }, [invoiceItems]);

  useEffect(() => {
    if (preselectedCustomerId) {
      setCustomerId(preselectedCustomerId);
    }
  }, [preselectedCustomerId]);

  useEffect(() => {
    fetch(`https://www.kenan.bybello.com/backend/api/customers.php?user_id=${user.id}`, {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => setCustomers(data));
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetch(`https://www.kenan.bybello.com/backend/api/products.php?user_id=${user.id}`)
        .then(res => res.json())
        .then(data => setProductSuggestions(data))
        .catch(error => console.error("Ürün önerileri çekilirken hata oluştu:", error));
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      fetch(`https://www.kenan.bybello.com/backend/api/last_invoice_number.php?user_id=${user.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setLastInvoiceNumber(data.last_invoice_number);
          } else {
            console.error("Son irsaliye numarası çekilirken hata oluştu:", data.error);
          }
        })
        .catch(error => console.error("Son irsaliye numarası çekilirken hata oluştu:", error));
    }
  }, [user]);

  // Edit mode: initialData'dan formu doldur
  useEffect(() => {
    if (editMode && initialData) {
      setEditingInvoiceId(initialData.id);
      setCustomerId(initialData.customer_id);
      setDate(initialData.date);
      setInvoiceNumber(initialData.invoice_number || '');
      // Kalemleri id ile eşle (gerekirse)
      setInvoiceItems(
        (initialData.items || []).map((item, idx) => ({
          id: idx + 1,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          description: item.description,
          item_total_price: parseFloat(item.quantity) * parseFloat(item.unit_price)
        }))
      );
    }
  }, [editMode, initialData]);

  const handleItemChange = (id, field, value) => {
    setInvoiceItems(prevItems =>
      prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate item_total_price if quantity or unit_price changes
          if (field === 'quantity' || field === 'unit_price') {
            const qty = parseFloat(updatedItem.quantity);
            const price = parseFloat(updatedItem.unit_price);
            updatedItem.item_total_price = !isNaN(qty) && !isNaN(price) ? qty * price : 0;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleAddItem = () => {
    setInvoiceItems(prevItems => [
      ...prevItems,
      { id: prevItems.length + 1, product_name: '', quantity: '', unit_price: '', description: '', item_total_price: 0 }
    ]);
  };

  const handleRemoveItem = (id) => {
    setInvoiceItems(prevItems => prevItems.filter(item => item.id !== id));
  };

  const handleSave = async (e, shouldPrint = false) => {
    e.preventDefault();
    setMessage('');
    const isValidItems = invoiceItems.every(item =>
      item.product_name && parseFloat(item.quantity) > 0 && parseFloat(item.unit_price) > 0
    );
    if (!isValidItems) {
      setMessage('Lütfen tüm ürün kalemleri için geçerli Ürün Adı, Adet ve Birim Fiyat girin.');
      return;
    }
    try {
      let response, data;
      if (editMode && editingInvoiceId) {
        // PUT ile güncelle
        response = await fetch('https://www.kenan.bybello.com/backend/api/invoices.php', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: editingInvoiceId,
            customer_id: customerId,
            date,
            invoice_items: invoiceItems.map(item => ({
              product_name: item.product_name,
              quantity: parseFloat(item.quantity),
              unit_price: parseFloat(item.unit_price),
              description: item.description
            })),
            user_id: userId
          })
        });
        data = await response.json();
      } else {
        // POST ile ekle
        response = await fetch('https://www.kenan.bybello.com/backend/api/invoices.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: customerId,
            date,
            invoice_items: invoiceItems.map(item => ({
              product_name: item.product_name,
              quantity: parseFloat(item.quantity),
              unit_price: parseFloat(item.unit_price),
              description: item.description
            })),
            user_id: userId
          })
        });
        data = await response.json();
      }
      if (data.success) {
        setMessage(editMode ? 'İrsaliye başarıyla güncellendi!' : 'İrsaliye başarıyla eklendi!');
        setCustomerId(preselectedCustomerId || '');
        setDate(() => {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        });
        setInvoiceItems([
          { id: 1, product_name: '', quantity: '', unit_price: '', description: '', item_total_price: 0 }
        ]);
        setOverallTotalPrice(0);
        setInvoiceNumber(data.invoice_number || '');
        setLastInvoiceNumber(data.invoice_number || lastInvoiceNumber);
        if (onInvoiceAdded) onInvoiceAdded();
        if (shouldPrint && (data.invoiceId || editingInvoiceId)) {
          navigate(`/invoice-print/${data.invoiceId || editingInvoiceId}`);
        }
      } else {
        setMessage(data.error || 'Bir hata oluştu.');
      }
    } catch (error) {
      setMessage('Bir hata oluştu.');
    }
  };

  return (
    <div>
      <h2>{editMode ? 'İrsaliye Düzenle' : 'Yeni İrsaliye Ekle'}</h2>
      {lastInvoiceNumber !== null && (
        <p className="text-muted mb-3">Son İrsaliye No: <strong>{lastInvoiceNumber || 'Yok'}</strong></p>
      )}
      <form>
        <div className="row mb-3">
          <div className="col-md-4">
            <label htmlFor="customerSelect" className="form-label mb-1">Müşteri Seç</label>
            <select
              id="customerSelect"
              className="form-select"
              value={customerId}
              onChange={e => setCustomerId(e.target.value)}
              required
              disabled={!!preselectedCustomerId}
            >
              <option value="">Müşteri Seç</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="invoiceNumberInput" className="form-label mb-1">İrsaliye No</label>
            <input
              type="text"
              className="form-control"
              id="invoiceNumberInput"
              value={invoiceNumber}
              readOnly
              placeholder="Oto. Oluşur"
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="dateInput" className="form-label mb-1">Tarih</label>
            <input
              type="date"
              className="form-control"
              id="dateInput"
              value={date}
              onChange={e => setDate(e.target.value)}
              required
            />
          </div>
        </div>

        <hr className="my-4" /> {/* Separator */}

        <h4>Ürün Kalemleri</h4>
        {invoiceItems.map((item, index) => (
          <div className="row mb-3 align-items-end" key={item.id}>
            <div className="col-md-2">
              <label htmlFor={`productNameInput_${item.id}`} className="form-label mb-1">Ürün Adı</label>
              <input
                type="text"
                className="form-control"
                id={`productNameInput_${item.id}`}
                placeholder="Ürün Adı"
                value={item.product_name}
                onChange={e => handleItemChange(item.id, 'product_name', e.target.value)}
                list="productSuggestions"
                required
              />
              {index === 0 && ( // Datalist only needs to be on one input
                <datalist id="productSuggestions">
                  {productSuggestions.filter(suggestion =>
                    suggestion.toLowerCase().includes(item.product_name.toLowerCase())
                  ).map((suggestion, idx) => (
                    <option key={idx} value={suggestion} />
                  ))}
                </datalist>
              )}
            </div>
            <div className="col-md-2">
              <label htmlFor={`descriptionInput_${item.id}`} className="form-label mb-1">Açıklama</label>
              <input
                type="text"
                className="form-control"
                id={`descriptionInput_${item.id}`}
                placeholder="Açıklama"
                value={item.description}
                onChange={e => handleItemChange(item.id, 'description', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor={`quantityInput_${item.id}`} className="form-label mb-1">Renk</label>
              <input
                type="number"
                className="form-control"
                id={`quantityInput_${item.id}`}
                placeholder="Adet"
                value={item.quantity !== '' ? parseInt(item.quantity, 10) : ''}
                onChange={e => handleItemChange(item.id, 'quantity', e.target.value)}
                required
              />
            </div>
            <div className="col-md-2">
              <label htmlFor={`unitPriceInput_${item.id}`} className="form-label mb-1">Birim Fiyat</label>
              <input
                type="number"
                className="form-control"
                id={`unitPriceInput_${item.id}`}
                placeholder="Birim Fiyat"
                value={item.unit_price}
                onChange={e => handleItemChange(item.id, 'unit_price', e.target.value)}
                required
              />
            </div>
            <div className="col-md-2"> {/* Added column for item total price */}
              <label htmlFor={`itemTotalPriceInput_${item.id}`} className="form-label mb-1">Toplam Tutar</label>
              <input
                type="text"
                className="form-control fw-bold"
                id={`itemTotalPriceInput_${item.id}`}
                value={item.item_total_price.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                readOnly
              />
            </div>
            <div className="col-md-2">
              <div className="form-label mb-1" style={{ visibility: 'hidden' }}>Placeholder</div>
              <button
                type="button"
                className="btn btn-danger w-100"
                onClick={() => handleRemoveItem(item.id)}
              >
                Sil
              </button>
            </div>
          </div>
        ))}

        {/* Overall Total Price at the bottom right */}
        <div className="row mb-3 mt-4 justify-content-end">
          <div className="col-md-3">
            <div className="d-flex justify-content-end align-items-center">
              <h5 className="me-2 mb-0">Genel Toplam Tutar:</h5>
              <input
                type="text"
                className="form-control fw-bold" style={{width: '150px'}}
                value={overallTotalPrice.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-end">
            {!editMode && (
              <>
                <button type="button" onClick={handleAddItem} className="btn btn-primary me-2">Ürün Ekle</button>
                <button type="button" onClick={(e) => handleSave(e, false)} className="btn btn-success me-2">Ekle</button>
                {/* <button type="button" onClick={(e) => handleSave(e, true)} className="btn btn-success">Kaydet ve Yazdır</button> */}
              </>
            )}
            {editMode && (
              <button type="button" onClick={(e) => handleSave(e, false)} className="btn btn-success">Güncelle</button>
            )}
          </div>
        </div>
      </form>
      {message && <div className="mt-3">{message}</div>}
    </div>
  );
}

export default InvoiceAdd;
